/* Los contenedores de un CRT y en qué MIC va cada uno.
 *
 * ── POR QUÉ ──────────────────────────────────────────────────────────────────
 *
 * 13/10, CRT AR.TCC.000025, siete contenedores de 40': salieron siete MIC, la
 * cuenta de bultos cerró (7 de 7) y aun así estaba mal. Dos MIC —camión 3 y
 * camión 4— decían el MISMO contenedor, con el mismo tractor y el mismo
 * acoplado, y otro contenedor del CRT no estaba en ningún MIC. El reparto no
 * lo podía ver: cuenta bultos, y un contenedor es un bulto como cualquier otro.
 *
 * Fabián, ese mismo día: «seguimos en etapa de entrenamiento y viendo que se
 * puede modificar para tipear lo menos posible y evitar el error de tipeo».
 *
 * ── AVISA, NO FRENA ─────────────────────────────────────────────────────────
 *
 * Nada de esto bloquea guardar ni emitir. Un camión puede hacer dos viajes del
 * mismo tránsito, y un número de contenedor escrito distinto en el CRT y en el
 * MIC puede ser el CRT el que está mal. Lo que no puede pasar es que nadie lo
 * vea: por eso se dice en el formulario, al lado de lo que hay que mirar, y en
 * la lista de tránsitos.
 *
 * ── DÓNDE ESTÁ ESCRITO CADA CONTENEDOR ──────────────────────────────────────
 *
 * En el CRT: en `contenedores` (la lista que trae el bot del BL) o, cuando el
 * BL no vino, escrito a mano en la mercadería — la pantalla del CRT lo pide
 * así («cargalos a mano en la mercadería»), y es el caso del AR.TCC.000026. Se
 * leen los dos.
 *
 * En el MIC no hay columna: va escrito en la 38 (descripción), a veces en la
 * 37 (precintos). En un consolidado, cada carta de porte tiene su 38: la de la
 * carátula es la del manifiesto, la de cada continuación es la de su renglón.
 *
 * ── SÓLO CONTENEDOR COMPLETO ────────────────────────────────────────────────
 *
 * En trasbordo la mercadería se pasa a un furgón: el BL trae los contenedores
 * pero el camión no los lleva, y un mismo contenedor se puede repartir entre
 * dos camiones y nombrarse en los dos MIC. Ahí no hay nada que controlar. Por
 * eso todo esto mira sólo los CRT que van en contenedor completo
 * (`tipo_carga`). Los CRT viejos sin `tipo_carga` cuentan si sus bultos son
 * contenedores (`bultos_clase`), que es como se cargaban.
 */

/* ── El número de un contenedor ───────────────────────────────────────────
 *
 * ISO 6346: tres letras del dueño, la categoría (U, J o Z), seis cifras y el
 * dígito verificador: EITU1705534. A mano se escribe también «EITU 170553-4»
 * o «EITU 1705534»; los tres son el mismo y se comparan sin los separadores.
 *
 * EL DÍGITO VERIFICADOR NO SE CONTROLA. Un número mal tipeado sigue siendo lo
 * que la persona quiso escribir, y es justo el que hay que poder comparar
 * contra el CRT para avisar que no coincide.
 *
 * Sin lookbehind (`(?<!…)`) ni `matchAll`: Safari entiende el primero recién
 * desde la 16.4, y una expresión que no entiende rompe la pantalla entera, no
 * sólo este aviso. */
const PATRON = /\b([A-Z]{3}[UJZ])[ .-]?(\d{6})[ .-]?(\d)\b/;

const unicos = (xs) => [...new Set(xs)];

/* Los contenedores que aparecen en un texto, sin repetir, en el orden en que
 * aparecen. */
export function contenedoresEn(texto) {
  const t = String(texto ?? "").toUpperCase();
  const salida = [];
  const re = new RegExp(PATRON.source, "g");
  for (let m = re.exec(t); m; m = re.exec(t)) salida.push(`${m[1]}${m[2]}${m[3]}`);
  return unicos(salida);
}

/* Si el CRT va en contenedor completo. Ver arriba: el trasbordo no cuenta. */
export function vaEnContenedores(crt) {
  const tipo = String(crt?.tipo_carga ?? "").trim().toLowerCase();
  if (tipo) return tipo === "contenedor";
  return /^CONTENEDOR/i.test(String(crt?.bultos_clase ?? "").trim());
}

/* Los contenedores del CRT: los de la lista del BL y los escritos en la
 * mercadería, sin repetir. Cada número de la lista se lee igual que el texto,
 * así «EITU 170553-4» del BL y «EITU1705534» del MIC son el mismo. */
export function contenedoresDelCrt(crt) {
  const lista = Array.isArray(crt?.contenedores) ? crt.contenedores : [];
  const deLaLista = lista.flatMap((c) =>
    contenedoresEn(typeof c === "string" ? c : c?.numero));
  return unicos([...deLaLista, ...contenedoresEn(crt?.mercaderia)]);
}

/* El contenedor del CRT cuando es UNO solo, o "" si son varios, ninguno o no
 * va en contenedor completo. Es el único caso en que se sabe sin preguntar
 * qué contenedor lleva el camión: el 38 del MIC lo trae escrito (ver
 * `descripcionDelCrt` en `shared/mic`). Con varios NO se deduce «el que
 * queda sin MIC»: sería creerle a los otros MIC, y estos avisos existen
 * porque a veces están mal. */
export function contenedorUnico(crt) {
  if (!vaEnContenedores(crt)) return "";
  const todos = contenedoresDelCrt(crt);
  return todos.length === 1 ? todos[0] : "";
}

/* El texto sin los números de contenedor, para la mercadería que los trae
 * escritos a mano (el AR.TCC.000026: «…AIRE ACONDICIONADO.\nEGHU9745023-
 * EITU9123946-…»). Un renglón que eran sólo números y guiones se va entero;
 * a uno que tenía texto se le sacan los números y los separadores que
 * quedan colgando. Los renglones sin contenedores quedan como estaban,
 * mayúsculas y minúsculas incluidas. Se reconocen igual que en
 * `contenedoresEn`. */
const SEPARADORES = /^[\s\-–—·,;/|]+|[\s\-–—·,;/|]+$/g;

export function sinContenedores(texto) {
  const re = new RegExp(PATRON.source, "gi");
  return String(texto ?? "")
    .split("\n")
    .map((l) => {
      const sin = l.replace(re, " ");
      if (sin === l) return l;
      const limpio = sin.replace(SEPARADORES, "").replace(/ {2,}/g, " ");
      return limpio || null;
    })
    .filter((l) => l !== null)
    .join("\n");
}

const RENGLONES = (mic) =>
  [...(mic?.crts || []).filter(Boolean)]
    .sort((a, b) => (Number(a.orden) || 99) - (Number(b.orden) || 99));

/* Lo que el MIC dice de ESTE CRT, en las casillas 37 y 38.
 *
 * Sin renglones es el manifiesto entero, sin mirar el `crt_id` (la misma regla
 * que `aporteA` en `shared/mic`). Con renglones, la carta de porte de ese CRT:
 * la carátula imprime la 37 y la 38 del manifiesto; una continuación, las de
 * su renglón. Así un consolidado con contenedores de dos CRT no le anota a uno
 * los del otro. */
export function textoDelMicPara(mic, crtId) {
  const de = (x) => [x?.descripcion, x?.precintos].filter(Boolean).join("\n");
  const reng = RENGLONES(mic);
  if (!reng.length) return de(mic);
  const i = reng.findIndex((l) => String(l.crt_id) === String(crtId));
  if (i === -1) return String(mic?.crt_id) === String(crtId) ? de(mic) : "";
  return i === 0 ? [de(mic), de(reng[0])].filter(Boolean).join("\n") : de(reng[i]);
}

export const contenedoresDelMic = (mic, crtId) =>
  contenedoresEn(textoDelMicPara(mic, crtId));

/* Un anulado no lleva nada: es la misma regla del reparto. */
const VIVOS = (mics) => (mics || []).filter((m) => m && m.estado !== "anulado");

/* ── Cómo está el tránsito ────────────────────────────────────────────────
 *
 * Para un CRT en contenedor completo y sus MIC:
 *
 *   sinMic      los contenedores del CRT que no figuran en ningún MIC vivo.
 *   repetidos   los contenedores que figuran en más de un MIC vivo.
 *   ajenos      MIC que nombran un contenedor que el CRT no tiene (lo más
 *               probable, un número mal tipeado de un lado o del otro).
 *   sinDecir    MIC que no nombran ningún contenedor.
 *
 * Los dos últimos están porque sin ellos «sin MIC» mentiría: el contenedor que
 * falta casi siempre es el que un MIC no escribió, o escribió mal. Y sólo se
 * dicen si el CRT tiene su lista: sin ella no hay contra qué comparar.
 *
 * `null` si el CRT no va en contenedor completo. */
export function estadoContenedores(crt, mics) {
  if (!crt || !vaEnContenedores(crt)) return null;
  const delCrt = contenedoresDelCrt(crt);
  const lleva = VIVOS(mics).map((mic) => ({
    mic,
    contenedores: contenedoresDelMic(mic, crt.id),
  }));

  const enMic = new Map();
  for (const l of lleva) {
    for (const c of l.contenedores) enMic.set(c, [...(enMic.get(c) || []), l.mic]);
  }

  return {
    contenedores: delCrt,
    sinMic: delCrt.filter((c) => !enMic.has(c)),
    repetidos: [...enMic]
      .filter(([, ms]) => ms.length > 1)
      .map(([numero, ms]) => ({ numero, mics: ms })),
    ajenos: delCrt.length
      ? lleva
        .map((l) => ({ mic: l.mic, numeros: l.contenedores.filter((c) => !delCrt.includes(c)) }))
        .filter((x) => x.numeros.length)
      : [],
    sinDecir: delCrt.length
      ? lleva.filter((l) => !l.contenedores.length).map((l) => l.mic)
      : [],
  };
}

/* ── Cómo se nombra un MIC en un aviso ────────────────────────────────────
 *
 * Por su número, que es lo que está en el papel, y el camión, que es lo que se
 * ve en la lista. Un borrador se dice: avisar contra uno que todavía no salió
 * es avisar igual, pero hay que saber que se puede borrar. */
function quien(m) {
  const extra = [
    m.hoja ? `camión ${m.hoja}` : "",
    m.estado === "borrador" ? "en borrador" : "",
  ].filter(Boolean).join(", ");
  return `${m.numero || "sin número"}${extra ? ` (${extra})` : ""}`;
}

const enumerar = (xs) =>
  xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}`;

const cualesMic = (ms) =>
  ms.length === 1 ? `el MIC ${quien(ms[0])}` : `los MIC ${enumerar(ms.map(quien))}`;

const CUANTOS = { 2: "dos", 3: "tres", 4: "cuatro" };

/* «Contenedores sin MIC: A y B.» `deQue` nombra el CRT cuando hace falta
 * decir de cuál (un consolidado lleva varios). */
export function textoSinMic(sinMic, deQue = "") {
  if (!sinMic?.length) return "";
  const cuales = sinMic.length === 1 ? "Contenedor" : "Contenedores";
  return `${cuales}${deQue ? ` ${deQue}` : ""} sin MIC: ${enumerar(sinMic)}.`;
}

/* Las líneas para la lista de tránsitos. `aviso` es lo que está mal; lo que
 * no lo es («sin MIC» mientras se reparte es lo normal) va sin marcar. */
export function lineasDelTransito(estado) {
  if (!estado) return [];
  const lineas = [];
  const { sinMic, repetidos, ajenos, sinDecir } = estado;
  if (sinMic.length) lineas.push({ aviso: false, texto: textoSinMic(sinMic) });
  for (const r of repetidos) {
    lineas.push({
      aviso: true,
      texto: `El contenedor ${r.numero} está en ${CUANTOS[r.mics.length] || r.mics.length} MIC: ${enumerar(r.mics.map(quien))}.`,
    });
  }
  for (const a of ajenos) {
    const varios = a.numeros.length > 1;
    lineas.push({
      aviso: true,
      texto: `El MIC ${quien(a.mic)} dice ${enumerar(a.numeros)}, que no ${varios ? "están" : "está"} entre los contenedores del CRT.`,
    });
  }
  if (sinDecir.length) {
    lineas.push({
      aviso: true,
      texto: sinDecir.length === 1
        ? `El MIC ${quien(sinDecir[0])} no dice qué contenedor lleva.`
        : `Los MIC ${enumerar(sinDecir.map(quien))} no dicen qué contenedor llevan.`,
    });
  }
  return lineas;
}

/* ── Al armar o corregir un MIC ───────────────────────────────────────────
 *
 * `mic` es el formulario tal como está en pantalla (sin guardar, o un emitido
 * que se corrige); `crts`, los CRT que lleva —el suyo y los de sus renglones—;
 * `micsDe(crtId)`, los MIC de cada CRT como los tiene la pantalla. El MIC
 * mismo se saca de esa lista por su `id`: corregir uno emitido no puede
 * avisar que choca con su propia versión guardada.
 *
 * Devuelve dos listas de textos, porque van en dos lugares del formulario: lo
 * del contenedor al lado de la 37 y la 38, lo del camión al lado de las
 * patentes.
 *
 * LA PATENTE SE COMPARA SIN ADORNOS («AH 904 AN» y «AH904AN» son la misma,
 * igual que en el padrón) y hacen falta LAS DOS, tractor y acoplado: el mismo
 * tractor con otro acoplado es otro equipo. */
const sinAdornos = (s) => String(s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

export function avisosAlArmar(mic, crts, micsDe) {
  const salida = { contenedores: [], camion: [] };
  if (!mic || mic.estado === "anulado") return salida;

  const placa = sinAdornos(mic.placa);
  const acoplado = sinAdornos(mic.acoplado_placa);
  const yaDichos = new Set();
  const vistos = new Set();

  for (const crt of crts || []) {
    if (!crt || vistos.has(String(crt.id)) || !vaEnContenedores(crt)) continue;
    vistos.add(String(crt.id));
    const donde = String(crt.id) === String(mic.crt_id)
      ? "de este CRT"
      : `del CRT ${crt.numero}`;
    const otros = VIVOS(micsDe(crt.id))
      .filter((o) => mic.id == null || String(o.id) !== String(mic.id));

    const mios = contenedoresDelMic(mic, crt.id);
    for (const c of mios) {
      const ya = otros.filter((o) => contenedoresDelMic(o, crt.id).includes(c));
      if (ya.length) {
        salida.contenedores.push(`El contenedor ${c} ya está en ${cualesMic(ya)} ${donde}.`);
      }
    }
    const delCrt = contenedoresDelCrt(crt);
    if (delCrt.length) {
      for (const c of mios.filter((x) => !delCrt.includes(x))) {
        salida.contenedores.push(
          `El contenedor ${c} no figura en el CRT ${crt.numero}: revisá el número.`,
        );
      }
    }

    if (placa && acoplado) {
      const mismos = otros.filter((o) =>
        !yaDichos.has(String(o.id))
        && sinAdornos(o.placa) === placa
        && sinAdornos(o.acoplado_placa) === acoplado);
      if (mismos.length) {
        mismos.forEach((o) => yaDichos.add(String(o.id)));
        salida.camion.push(
          `El camión ${String(mic.placa).trim()} con el acoplado ${String(mic.acoplado_placa).trim()} ya está en ${cualesMic(mismos)} ${donde}.`,
        );
      }
    }
  }
  return salida;
}

/* Los MIC de un CRT con el formulario abierto en lugar de su versión guardada
 * —o sumado, si todavía no se guardó—. Es lo que hace que «sin MIC» cambie
 * mientras se escribe la 38. */
export function conElFormulario(mics, mic) {
  if (!mic) return mics || [];
  const resto = (mics || []).filter((m) => mic.id == null || String(m.id) !== String(mic.id));
  return [...resto, mic];
}
